import { useState } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

const faqs = [
  {
    q: '韩漫吧是什么网站？',
    a: '韩漫吧是一个专注于韩国漫画的免费在线阅读平台。我们提供海量韩国漫画资源，支持下拉式阅读体验，致力于为漫画爱好者打造一个轻松愉快的阅读和交流空间。所有漫画均为免费阅读，无需注册付费。',
  },
  {
    q: '韩漫吧的漫画资源是否免费？',
    a: '是的，韩漫吧所有漫画资源完全免费。您无需注册账号或支付任何费用即可在线浏览所有漫画内容。我们坚持免费服务，让每一位漫画爱好者都能轻松享受阅读的乐趣。',
  },
  {
    q: '如何找到我喜欢的漫画？',
    a: '您可以通过首页的分类导航浏览不同类型的漫画，也可以使用搜索功能直接查找。我们提供恋爱、奇幻、动作、悬疑等多种分类，并按人气、评分、更新时间等维度排序，方便您快速找到心仪的作品。',
  },
  {
    q: '韩漫吧支持手机阅读吗？',
    a: '当然支持！韩漫吧采用响应式设计，完美适配手机、平板和电脑等多种设备。无论在通勤路上还是家中放松，您都可以随时随地享受流畅的下拉式漫画阅读体验。',
  },
  {
    q: '漫画更新频率如何？',
    a: '韩漫吧每日更新，保持与韩国漫画源同步。热门连载作品会在第一时间更新最新话，已完结作品也会持续补充。您可以关注收藏喜欢的漫画，第一时间获取更新通知。',
  },
];

const testimonials = [
  {
    name: '小漫迷',
    avatar: 'ri-user-3-line',
    role: '忠实用户 / 3年',
    content: '韩漫吧是我每天必刷的网站！漫画资源特别全，更新也快。最喜欢下拉式阅读的设计，看漫画特别流畅，推荐给所有韩漫爱好者！',
  },
  {
    name: '月下独酌',
    avatar: 'ri-user-3-line',
    role: '漫画博主',
    content: '作为漫画推荐博主，韩漫吧的分类和推荐系统做得很好。每部漫画的信息都很详细，评分系统也很靠谱，帮忙发现了不少冷门好作品。',
  },
  {
    name: '草莓味',
    avatar: 'ri-user-3-line',
    role: '资深读者 / 5年',
    content: '用了很多漫画网站，韩漫吧的体验是最好的。界面干净清爽，没有烦人的广告，分类清晰，找漫画特别方便。免费这一点真的很良心！',
  },
];

const milestones = [
  { year: '2023', title: '平台创立', desc: '韩漫吧正式上线，致力于免费韩国漫画分享' },
  { year: '2024', title: '资源突破', desc: '收录漫画超过1000部，日活跃用户突破10万' },
  { year: '2025', title: '体验升级', desc: '推出下拉式阅读模式，全面优化移动端体验' },
  { year: '2026', title: '持续成长', desc: '漫画资源超过2000部，成为国内领先的韩漫阅读平台' },
];

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormSubmitted(true);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData(form);
      if (honeypotInput) formData.delete('website_alt');

      const formUrl = 'https://readdy.ai/api/form/d9ot0hqftmtnfcfh0dfg';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        setFormError(msg.includes('spam') ? '提交失败，请稍后再试' : msg);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanba.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: '关于韩漫吧 - 免费韩国漫画在线阅读平台',
        url: 'https://hanmanba.com/about',
        description: '韩漫吧致力于推荐好看韩国漫画，提供免费漫画大全和下拉式在线阅读服务，氛围轻松，是漫画同好分享与发现心仪作品的角落。',
        isPartOf: { '@type': 'WebSite', name: '韩漫吧' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title="关于韩漫吧 - 免费韩国漫画在线阅读平台"
        description="韩漫吧致力于推荐好看韩国漫画，提供免费漫画大全和下拉式在线阅读服务。了解韩漫吧的品牌故事、发展历程和联系方式。"
        keywords="韩漫吧,关于韩漫吧,免费漫画平台,韩国漫画网站,漫画在线阅读"
        canonicalUrl="https://hanmanba.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main className="pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Intro */}
          <section className="mb-14">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-500">
                <i className="ri-book-open-line text-2xl text-white"></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                关于<span className="text-primary-600">韩漫吧</span>
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                韩漫吧创立于2023年，是一个专注于韩国漫画的免费在线阅读平台。
                我们致力于为广大漫画爱好者提供最优质的韩漫资源和最舒适的阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-eye-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  让每一位漫画爱好者都能轻松找到并阅读自己喜欢的韩国漫画，打造中文世界最好的韩漫阅读社区。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-heart-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  持续收录优质韩国漫画资源，提供免费、流畅、美观的在线阅读服务，为漫画文化的传播贡献力量。
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-user-heart-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-2">我们的承诺</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  永远坚持免费服务，不断优化阅读体验，用心倾听每一位用户的反馈和建议。
                </p>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden md:block"></div>
              <div className="space-y-6">
                {milestones.map((item, index) => (
                  <div key={item.year} className={`flex items-center gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div className="bg-background-50 rounded-xl p-4 border border-background-200/70 inline-block">
                        <h3 className="font-heading text-base text-foreground-900">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-white font-heading text-sm shrink-0 z-10 shadow-sm">
                      {item.year}
                    </div>
                    <div className="flex-1 md:hidden">
                      <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
                        <h3 className="font-heading text-base text-foreground-900">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${t.avatar} text-lg text-accent-600`}></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-900 block">{t.name}</span>
                      <span className="text-xs text-foreground-500">{t.role}</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="space-y-3 max-w-3xl mx-auto">
              {faqs.map((faq) => (
                <details key={faq.q} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何建议或问题？欢迎通过以下表单联系我们，我们会尽快回复。
            </p>

            {formSubmitted ? (
              <div className="bg-background-50 rounded-xl p-8 border border-background-200/70 text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-check-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-50 rounded-xl p-6 border border-background-200/70" data-readdy-form>
                {formError && (
                  <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
                    {formError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="留言主题"
                  />
                </div>
                <div className="mb-5">
                  <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请描述您的问题或建议（500字以内）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  className="spam-hp"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> 提交中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line"></i> 发送留言
                    </span>
                  )}
                </button>
              </form>
            )}
          </section>
        </div>
      </main>
    </>
  );
}